import React, { useState, useMemo, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import SearchFilterBar from './components/SearchFilterBar';
import CustomerTableRow from './components/CustomerTableRow';
import CustomerProfileModal from './components/CustomerProfileModal';
import AddCustomerModal from './components/AddCustomerModal';
import { supabase } from '../../lib/supabase';

const CustomerDatabase = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    status: 'all',
    location: 'all',
    serviceType: 'all'
  });
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [locationFilter, setLocationFilter] = useState('all');
  const itemsPerPage = 10;

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        ?.from('customers')
        ?.select(`
          *,
          customer_equipment(*),
          customer_service_history(*)
        `)
        ?.order('name');

      if (error) throw error;

      // Transform data to match existing structure
      const transformedCustomers = (data || [])?.map(customer => ({
        id: customer?.id,
        name: customer?.name,
        email: customer?.email,
        phone: customer?.phone,
        company: customer?.company,
        location: customer?.location,
        address: customer?.address,
        status: customer?.status?.charAt(0)?.toUpperCase() + customer?.status?.slice(1),
        totalServices: customer?.total_services || 0,
        totalRevenue: customer?.total_revenue || 0,
        equipmentCount: customer?.equipment_count || 0,
        lastService: customer?.customer_service_history?.[0]?.service_date || customer?.created_at,
        equipment: (customer?.customer_equipment || [])?.map(eq => ({
          name: eq?.name,
          model: eq?.model,
          serial: eq?.serial,
          installDate: eq?.install_date,
          lastService: eq?.last_service
        })),
        serviceHistory: (customer?.customer_service_history || [])?.map(sh => ({
          type: sh?.type,
          description: sh?.description,
          date: sh?.service_date,
          technician: sh?.technician,
          ticketId: sh?.ticket_id,
          cost: sh?.cost
        }))
      }));

      setCustomers(transformedCustomers);
    } catch (error) {
      console.error('Error fetching customers:', error?.message);
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredCustomers = useMemo(() => {
    let filtered = [...customers];

    if (searchQuery) {
      const query = searchQuery?.toLowerCase();
      filtered = filtered?.filter(customer =>
        customer?.name?.toLowerCase()?.includes(query) ||
        customer?.email?.toLowerCase()?.includes(query) ||
        customer?.company?.toLowerCase()?.includes(query) ||
        customer?.phone?.includes(query)
      );
    }

    if (filters?.status !== 'all') {
      filtered = filtered?.filter(customer => customer?.status === filters?.status);
    }

    if (filters?.location !== 'all') {
      filtered = filtered?.filter(customer => customer?.location === filters?.location);
    }

    if (sortConfig?.key) {
      filtered?.sort((a, b) => {
        let aValue = a?.[sortConfig?.key];
        let bValue = b?.[sortConfig?.key];

        if (sortConfig?.key === 'lastService') {
          aValue = new Date(aValue);
          bValue = new Date(bValue);
        }

        if (aValue < bValue) {
          return sortConfig?.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig?.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return filtered;
  }, [customers, searchQuery, filters, sortConfig]);

  const paginatedCustomers = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredCustomers?.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredCustomers, currentPage]);

  const totalPages = Math.ceil(filteredCustomers?.length / itemsPerPage);

  const locationFilterOptions = useMemo(() => {
    const locations = [...new Set(customers?.map(c => c?.location).filter(Boolean))];
    return locations?.map(loc => ({
      value: loc,
      label: loc,
      description: ''
    }));
  }, [customers]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev?.key === key && prev?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    setCurrentPage(1);
  };

  const handleFilter = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handleViewDetails = (customer) => {
    setSelectedCustomer(customer);
  };

  const handleSaveCustomer = async (updatedCustomer) => {
    try {
      const { error } = await supabase
        ?.from('customers')
        ?.update({
          name: updatedCustomer?.name,
          email: updatedCustomer?.email,
          phone: updatedCustomer?.phone,
          company: updatedCustomer?.company,
          location: updatedCustomer?.location,
          address: updatedCustomer?.address,
          status: updatedCustomer?.status?.toLowerCase()
        })
        ?.eq('id', updatedCustomer?.id);

      if (error) throw error;

      // Refresh customer list
      await fetchCustomers();
      setSelectedCustomer(null);
    } catch (error) {
      console.error('Error updating customer:', error?.message);
      alert('Failed to update customer. Please try again.');
    }
  };

  const handleAddCustomer = async (newCustomer) => {
    try {
      const { data, error } = await supabase
        ?.from('customers')
        ?.insert([{
          name: newCustomer?.name,
          email: newCustomer?.email,
          phone: newCustomer?.phone,
          company: newCustomer?.company,
          location: newCustomer?.location,
          address: newCustomer?.address,
          status: newCustomer?.status?.toLowerCase()
        }])
        ?.select()
        ?.single();

      if (error) throw error;

      // Refresh customer list to show the new customer
      await fetchCustomers();
      setShowAddModal(false);
    } catch (error) {
      console.error('Error adding customer:', error?.message);
      alert('Failed to add customer. Please try again.');
    }
  };

  const handleQuickAction = (customer, action) => {
    console.log(`Quick action: ${action} for customer:`, customer?.name);
  };

  const getSortIcon = (key) => {
    if (sortConfig?.key !== key) return 'ChevronsUpDown';
    return sortConfig?.direction === 'asc' ? 'ChevronUp' : 'ChevronDown';
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="px-4 md:px-6 lg:px-8 py-6 md:py-8 max-w-[1600px] mx-auto">
        <div className="mb-6 md:mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 md:w-12 md:h-12 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Users" size={24} color="var(--color-primary)" />
            </div>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-semibold text-foreground">
              Customer Database
            </h1>
          </div>
          <p className="text-sm md:text-base text-muted-foreground ml-0 md:ml-[52px] lg:ml-[60px]">
            Manage customer profiles, service history, and equipment inventory
          </p>
        </div>

        <div className="space-y-6">
          <SearchFilterBar
            onSearch={handleSearch}
            onFilter={handleFilter}
            onAddCustomer={() => setShowAddModal(true)}
          />

          <div className="bg-card rounded-xl border border-border shadow-elevation-1">
            <div className="p-4 md:p-6 border-b border-border">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground">
                    Customer List
                  </h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    {filteredCustomers?.length} {filteredCustomers?.length === 1 ? 'customer' : 'customers'} found
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {locationFilterOptions?.length > 0 && (
                    <div className="flex items-center gap-2">
                      <Icon name="MapPin" size={16} color="var(--color-warning)" />
                      <select
                        value={locationFilter}
                        onChange={(e) => { setLocationFilter(e?.target?.value); setCurrentPage(1); }}
                        className="px-3 py-1.5 border border-input rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                      >
                        <option value="all">All Locations</option>
                        {locationFilterOptions?.map(loc => (
                          <option key={loc?.value} value={loc?.value}>
                            {loc?.label}{loc?.description ? ` (${loc?.description})` : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  <Button variant="outline" size="sm" iconName="Download" iconPosition="left">
                    Export
                  </Button>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50 border-b border-border">
                  <tr>
                    <th className="px-4 md:px-6 py-3 text-left">
                      <button
                        onClick={() => handleSort('name')}
                        className="flex items-center gap-2 font-medium text-sm text-muted-foreground hover:text-foreground transition-smooth"
                      >
                        Customer
                        <Icon name={getSortIcon('name')} size={16} color="currentColor" />
                      </button>
                    </th>
                    <th className="px-4 md:px-6 py-3 text-left hidden lg:table-cell">
                      <button
                        onClick={() => handleSort('company')}
                        className="flex items-center gap-2 font-medium text-sm text-muted-foreground hover:text-foreground transition-smooth"
                      >
                        Company
                        <Icon name={getSortIcon('company')} size={16} color="currentColor" />
                      </button>
                    </th>
                    <th className="px-4 md:px-6 py-3 text-left hidden md:table-cell">
                      <span className="font-medium text-sm text-muted-foreground">Phone</span>
                    </th>
                    <th className="px-4 md:px-6 py-3 text-left hidden xl:table-cell">
                      <button
                        onClick={() => handleSort('location')}
                        className="flex items-center gap-2 font-medium text-sm text-muted-foreground hover:text-foreground transition-smooth"
                      >
                        Location
                        <Icon name={getSortIcon('location')} size={16} color="currentColor" />
                      </button>
                    </th>
                    <th className="px-4 md:px-6 py-3 text-left hidden lg:table-cell">
                      <button
                        onClick={() => handleSort('lastService')}
                        className="flex items-center gap-2 font-medium text-sm text-muted-foreground hover:text-foreground transition-smooth"
                      >
                        Last Service
                        <Icon name={getSortIcon('lastService')} size={16} color="currentColor" />
                      </button>
                    </th>
                    <th className="px-4 md:px-6 py-3 text-left">
                      <button
                        onClick={() => handleSort('status')}
                        className="flex items-center gap-2 font-medium text-sm text-muted-foreground hover:text-foreground transition-smooth"
                      >
                        Status
                        <Icon name={getSortIcon('status')} size={16} color="currentColor" />
                      </button>
                    </th>
                    <th className="px-4 md:px-6 py-3 text-left">
                      <span className="font-medium text-sm text-muted-foreground">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedCustomers?.map(customer => (
                    <CustomerTableRow
                      key={customer?.id}
                      customer={customer}
                      onViewDetails={handleViewDetails}
                      onQuickAction={handleQuickAction}
                    />
                  ))}
                </tbody>
              </table>

              {filteredCustomers?.length === 0 && (
                <div className="py-12 text-center">
                  <Icon name="Users" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
                  <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                    No customers found
                  </h3>
                  <p className="text-sm text-muted-foreground mb-6">
                    Try adjusting your search or filters
                  </p>
                  <Button variant="outline" onClick={() => setShowAddModal(true)}>
                    Add First Customer
                  </Button>
                </div>
              )}
            </div>

            {totalPages > 1 && (
              <div className="p-4 md:p-6 border-t border-border">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="text-sm text-muted-foreground">
                    Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredCustomers?.length)} of {filteredCustomers?.length} customers
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                    >
                      <Icon name="ChevronLeft" size={16} color="currentColor" />
                    </Button>
                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        return (
                          <button
                            key={pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                            className={`w-8 h-8 rounded-lg font-medium text-sm transition-smooth ${
                              currentPage === pageNum
                                ? 'bg-primary text-primary-foreground'
                                : 'text-foreground hover:bg-muted'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                    >
                      <Icon name="ChevronRight" size={16} color="currentColor" />
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
      {selectedCustomer && (
        <CustomerProfileModal
          customer={selectedCustomer}
          onClose={() => setSelectedCustomer(null)}
          onSave={handleSaveCustomer}
        />
      )}
      {showAddModal && (
        <AddCustomerModal
          onClose={() => setShowAddModal(false)}
          onSave={handleAddCustomer}
        />
      )}
    </div>
  );
};

export default CustomerDatabase;